import React, { useEffect, useState } from 'react';
import classNames from 'clsx';
import { useService } from '@wix/services-manager-react';
import { SiteScrollBlockerDefinition } from '@wix/viewer-service-site-scroll-blocker/definition';
import { isEmptyObject } from '../../../../../utils/dataUtils';
import Button from '../../../Button/component';
import type {
  HamburgerMenuProps,
  MenuProps,
  MenuStates,
} from '../../Menu.types';
import { selectors, MenuAnimationName } from '../../constants';
import { NavbarMenu } from '../Navbar';
import classes from './Hamburger.module.scss';
import { useFocusTrap } from '../../../../../utils/hooks/useFocusTrap';
import { useAnimationState } from '../MenuContent/MenuItem/useAnimationState';
import {
  ANIMATION_PACKAGE_CONFIG,
  HAMBURGER_MENU_ANIMATION_CONFIG,
  INITIAL_HAMBURGER_MENU_ANIMATION_STATE,
} from '../../animations';
import animationClasses from './HamburgerAnimation.module.scss';

export const HamburgerMenu: React.FC<MenuProps & MenuStates> = ({
  id,
  animationName = MenuAnimationName.None,
  hamburgerMenuOpen: isOpenProp,
  onItemClick,
  onItemDblClick,
  onItemMouseIn,
  onItemMouseOut,
  elementProps: {
    navbar: navbarProps,
    hamburgerOpenButton: hamburgerOpenButtonProps,
    hamburgerMenu: {
      content,
      headerContent,
      elementProps: {
        hamburgerCloseButton: hamburgerCloseButtonProps,
      } = {} as NonNullable<HamburgerMenuProps['elementProps']>,
    } = {
      isOpen: false,
    },
  },
}) => {
  const siteScrollBlocker = useService(SiteScrollBlockerDefinition);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    setIsOpen(!!isOpenProp);
  }, [isOpenProp]);

  useEffect(() => {
    if (isOpen) {
      siteScrollBlocker.setSiteScrollingBlocked(true, id);
    } else {
      siteScrollBlocker.setSiteScrollingBlocked(false, id);
    }
    return () => {
      siteScrollBlocker.setSiteScrollingBlocked(false, id);
    };
  }, [isOpen, siteScrollBlocker, id]);

  const { animationState, initEnterAnimation, initExitAnimation } =
    useAnimationState(
      HAMBURGER_MENU_ANIMATION_CONFIG,
      INITIAL_HAMBURGER_MENU_ANIMATION_STATE,
    );

  const setIsMenuOpen = React.useCallback(
    (newOpenValue: boolean) => {
      if (newOpenValue === isOpen) {
        return;
      }

      const getHamburgerMenuAnimationName = () => {
        const hamburgerMenuAnimationName =
          ANIMATION_PACKAGE_CONFIG[animationName].hamburgerMenuAnimationName;

        return hamburgerMenuAnimationName;
      };

      const hamburgerMenuAnimationName = getHamburgerMenuAnimationName();
      if (newOpenValue) {
        setIsOpen(true);
        initEnterAnimation(hamburgerMenuAnimationName);
      } else {
        initExitAnimation(hamburgerMenuAnimationName, () => setIsOpen(false));
      }
    },
    [isOpen, setIsOpen, initEnterAnimation, initExitAnimation, animationName],
  );

  const closeMenu = React.useCallback(
    () => setIsMenuOpen(false),
    [setIsMenuOpen],
  );

  const { containerRef: focusTrapRef } = useFocusTrap<HTMLDivElement>(isOpen);

  return (
    <>
      <Button
        {...hamburgerOpenButtonProps}
        className={classNames(
          selectors.hamburgerMenuOpenButton,
          classes.openButton,
        )}
        onClick={() => setIsMenuOpen(true)}
      />
      {isOpen && (
        <div
          className={classNames(
            selectors.hamburgerMenu,
            classes.dialog,
            animationClasses.dialog,
          )}
          data-animation-name={animationState.name}
          data-animation-state={animationState.phase}
          ref={focusTrapRef}
          onKeyDown={e => e.key === 'Escape' && closeMenu()}
        >
          <div className={classNames(classes.dialogContent)}>
            <header className={classes.header}>
              <div
                className={classNames(
                  selectors.hamburgerMenuHeaderContent,
                  classes.headerContent,
                )}
              >
                {headerContent}
              </div>
              <Button
                {...hamburgerCloseButtonProps}
                className={classNames(
                  selectors.hamburgerMenuCloseButton,
                  classes.closeButton,
                )}
                onClick={closeMenu}
              />
            </header>
            <main className={classes.main}>
              <NavbarMenu
                id={id}
                className={classes.navbar}
                {...navbarProps}
                onItemClick={(event, item) => {
                  if (!isEmptyObject(item.link)) {
                    closeMenu();
                  }
                  onItemClick?.(event, item);
                }}
                onItemDblClick={onItemDblClick}
                onItemMouseIn={onItemMouseIn}
                onItemMouseOut={onItemMouseOut}
              />
              <div
                className={classNames(
                  selectors.hamburgerMenuContent,
                  classes.content,
                )}
              >
                {content}
              </div>
            </main>
          </div>
          <div className={classNames(classes.backdrop)} onClick={closeMenu} />
        </div>
      )}
    </>
  );
};
